import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Radar, AlertTriangle, Zap, Wrench, Lightbulb, RefreshCw, CheckCircle2, Clock, Pause, Play, Plug } from "lucide-react";
import { Card } from "@/components/common/PageHeader";
import { Loading, Stat } from "@/pages/_shared";
import { toast } from "sonner";
import api from "@/lib/api";

const TYPE_META = {
  problem: { icon: AlertTriangle, cls: "bg-red-50 text-red-600" },
  bottleneck: { icon: Clock, cls: "bg-amber-50 text-amber-700" },
  system: { icon: Wrench, cls: "bg-blue-50 text-[#1a5fd0]" },
  opportunity: { icon: Lightbulb, cls: "bg-emerald-50 text-emerald-600" },
};
const SEV_CLS = { high: "bg-red-100 text-red-700", medium: "bg-amber-100 text-amber-700", low: "bg-slate-100 text-slate-500" };

export default function Situations() {
  const [data, setData] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const navigate = useNavigate();
  const load = useCallback(() => { api.get("/situations/overview").then(({ data }) => setData(data)); }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading />;
  const { situations, mandates, stats } = data;
  const open = situations.filter((s) => ["open", "in_progress"].includes(s.status));

  const runDetect = async () => {
    setDetecting(true);
    try { const { data } = await api.post("/situations/detect"); toast.success(`Scan complete · ${data.created} new, ${data.updated} updated`); load(); }
    catch { toast.error("Could not run detectors."); }
    finally { setDetecting(false); }
  };
  const setStatus = async (id, status) => { try { await api.patch(`/situations/${id}`, { status }); load(); } catch { toast.error("Could not update."); } };
  const toggleMandate = async (m) => { try { await api.patch(`/situations/mandates/${m.id}`, { status: m.status === "active" ? "paused" : "active" }); load(); } catch { toast.error("Could not update."); } };

  return (
    <div className="animate-fade-up space-y-6" data-testid="situations-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-head text-2xl font-extrabold tracking-tight text-slate-900"><Radar size={22} className="text-[#b0862f]" /> Situations &amp; Mandates</h1>
          <p className="mt-1 text-[13px] text-slate-500">The business runs itself through standing responsibilities and detected situations.</p>
        </div>
        <button onClick={runDetect} disabled={detecting} data-testid="run-detectors-btn"
          className="inline-flex items-center gap-2 rounded-lg bg-[#080d1a] px-4 py-2.5 font-head text-[13px] font-bold text-white hover:bg-[#1a5fd0] disabled:opacity-60">
          <RefreshCw size={15} className={detecting ? "animate-spin" : ""} /> {detecting ? "Scanning…" : "Run detectors"}
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <Stat label="Open situations" value={stats.open} tone={stats.open ? "red" : "green"} />
        <Stat label="High severity" value={stats.high} tone={stats.high ? "red" : "slate"} />
        <Stat label="Active mandates" value={stats.mandates_active} tone="blue" />
        <Stat label="Not connected" value={stats.not_connected} tone="slate" />
      </div>

      {/* Open situations */}
      <div>
        <div className="mb-2 font-mono text-[10px] font-bold uppercase tracking-wider text-slate-400">Open situations</div>
        {open.length === 0 ? (
          <Card className="flex items-center gap-3 p-6 text-[13px] text-slate-500"><CheckCircle2 size={18} className="text-emerald-500" /> Nothing needs attention. Run detectors to scan again.</Card>
        ) : (
          <div className="space-y-3">
            {open.map((s) => {
              const meta = TYPE_META[s.type] || TYPE_META.system;
              const Icon = meta.icon;
              return (
                <Card key={s.id} className="p-4" data-testid="situation-card">
                  <div className="flex items-start gap-3">
                    <span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${meta.cls}`}><Icon size={17} /></span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-head text-[14px] font-bold text-slate-900">{s.title}</span>
                        <span className={`rounded-full px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider ${SEV_CLS[s.severity] || SEV_CLS.low}`}>{s.severity}</span>
                        <span className="font-mono text-[9px] uppercase tracking-wider text-slate-400">{s.type} · {s.source}</span>
                      </div>
                      <p className="mt-1 text-[13px] text-slate-600">{s.description}</p>
                      <div className="mt-2 flex flex-wrap items-center gap-3">
                        {s.cta && <button onClick={() => navigate(s.cta)} data-testid="situation-cta" className="text-[12px] font-semibold text-[#1a5fd0] hover:underline">Open {s.source} →</button>}
                        <button onClick={() => setStatus(s.id, "resolved")} data-testid="resolve-situation" className="text-[12px] font-semibold text-emerald-600 hover:underline">Mark resolved</button>
                        <button onClick={() => setStatus(s.id, "dismissed")} className="text-[12px] font-semibold text-slate-400 hover:underline">Dismiss</button>
                        <span className="ml-auto font-mono text-[10px] text-slate-400">→ {s.assigned_to}</span>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Standing responsibilities */}
      <div>
        <div className="mb-2 font-mono text-[10px] font-bold uppercase tracking-wider text-slate-400">Standing responsibilities</div>
        <div className="space-y-2">
          {mandates.map((m) => (
            <Card key={m.id} className="flex items-center justify-between gap-3 p-4" data-testid="mandate-row">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-head text-[14px] font-bold text-slate-900">{m.name}</span>
                  {m.connected ? (
                    <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-emerald-600">Connected</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-slate-500"><Plug size={9} /> Not connected</span>
                  )}
                </div>
                <div className="mt-0.5 text-[12px] text-slate-500">{m.description}</div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-slate-400">
                  {m.cadence} · {m.owner}{m.last_run ? ` · ran ${new Date(m.last_run).toLocaleString()}` : ""}{!m.connected && m.requires ? ` · needs ${m.requires}` : ""}
                </div>
              </div>
              {m.connected && (
                <button onClick={() => toggleMandate(m)} data-testid="toggle-mandate"
                  className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-semibold ${m.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                  {m.status === "active" ? <><Pause size={13} /> Active</> : <><Play size={13} /> Paused</>}
                </button>
              )}
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
